import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  SalaryAdvanceEmployeeProfileFragment,
  SalaryAdvanceEmployeeProfilesCollectionDocument,
  SalaryAdvanceEmployeeProfilesCollectionQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMemo } from 'modules/core/hooks/useMemo'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import {
  getEmployeeStatusLabel,
  getEmployeeStatusVariant,
} from 'modules/salary_advance/lib/employeeHelpers'

export const EmployeesTable = () => {
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'

  const dataSourceOptions = useMemo(() => {
    return {
      document: SalaryAdvanceEmployeeProfilesCollectionDocument,
      fetchPolicy: 'cache-and-network' as const,
      mapData: (data: SalaryAdvanceEmployeeProfilesCollectionQuery) => {
        return data.salaryAdvance.employeeProfilesConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (data: SalaryAdvanceEmployeeProfilesCollectionQuery) => {
        const count = data.salaryAdvance.employeeProfilesConnection.totalCount ?? 0
        return { total: count }
      },
      mapVariables: () => ({}),
    }
  }, [])

  const source = useGraphqlDataCollectionSource(dataSourceOptions)

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('common.employee'),
                render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                  if (!item.employeeName) {
                    return { type: 'text', value: '-' }
                  }

                  return {
                    type: 'person',
                    value: {
                      firstName: item.employeeName.firstName,
                      lastName: item.employeeName.lastName,
                      src: buildUrl(item.employeeName.avatar?.url ?? ''),
                    },
                  }
                },
              },
              {
                label: i18n.t('common.status'),
                render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                  return {
                    type: 'status',
                    value: {
                      label: getEmployeeStatusLabel(item.status),
                      status: getEmployeeStatusVariant(item.status),
                    },
                  }
                },
              },
              {
                label: i18n.t('salary_advance.employee.table.columns.net_salary'),
                render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                  return {
                    type: 'text',
                    value: item.netSalaryCents
                      ? MoneyWithCurrency({
                          amount: item.netSalaryCents,
                          currency: item.currency ?? 'EUR',
                          locale,
                        })
                      : '-',
                  }
                },
              },
              {
                label: i18n.t('salary_advance.employee.table.columns.monthly_maximum_accrual'),
                render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                  return {
                    type: 'text',
                    value: item.maxMonthlyAccrualCents
                      ? MoneyWithCurrency({
                          amount: item.maxMonthlyAccrualCents,
                          currency: item.currency ?? 'EUR',
                          locale,
                        })
                      : '-',
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
